import mixpanel from "mixpanel-browser";
import { logger } from "../utils/logger";
import { buildProxyUrl, shouldUseDirectAnalytics } from "../utils/urlUtils";

const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN;
const mixpanelSecret = import.meta.env.VITE_MIXPANEL_API_SECRET;
const mixpanelEnvironment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";
/** Session Replay only on Production Mixpanel projects (dev/staging = 0%). */
const isProductionMixpanel = mixpanelEnvironment === "production";
const sessionReplayPercent = isProductionMixpanel ? 100 : 0;

// Singleton pattern for Mixpanel initialization
class MixpanelSingleton {
  private static instance: MixpanelSingleton;
  private initialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;

  private constructor() {}

  public static getInstance(): MixpanelSingleton {
    if (!MixpanelSingleton.instance) {
      MixpanelSingleton.instance = new MixpanelSingleton();
    }
    return MixpanelSingleton.instance;
  }

  public async initialize(): Promise<void> {
    // If already initialized, return immediately
    if (this.initialized) {
      return Promise.resolve();
    }

    // If initialization is in progress, return the existing promise
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Start initialization
    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    if (!mixpanelToken) {
      logger.warn(
        "Mixpanel Token is missing or appears to be a placeholder. Mixpanel will be disabled.",
      );
      return;
    }

    const retries = 3;

    for (let i = 0; i < retries; i++) {
      try {
        const config: any = {
          debug: import.meta.env.DEV,
          autocapture: {
            pageview: "full-url",
            click: true,
            input: true,
            rage_click: false,
            scroll: true,
            submit: true,
            capture_text_content: true,
          },
          secret: mixpanelSecret,
          persistence: "localStorage",
          record_sessions_percent: sessionReplayPercent,
          record_max_ms: 3600000, // 60 minutes in milliseconds
          record_heatmap_data: isProductionMixpanel,
          // Only mask explicitly marked sensitive content in replay.
          // Sensitive nodes are tagged in src/utils/privacy.ts with data-mp-mask="true".
          record_mask_text_selector: '[data-mp-mask="true"]',
          record_block_selector: '[data-mp-block="true"]',
          advanced_config: {
            enable_session_replay: isProductionMixpanel,
          },
        };

        // Use proxy for all remote environments (dev, stage, production), direct for localhost only
        if (!shouldUseDirectAnalytics()) {
          // Use smart proxy for ALL Mixpanel endpoints
          const proxyBase = buildProxyUrl("mixpanel");
          config.api_hosts = {
            record: proxyBase,
            engage: proxyBase,
            decide: proxyBase,
            api: proxyBase,
            track: proxyBase,
            batch: proxyBase,
            identify: proxyBase,
            people: proxyBase,
            groups: proxyBase,
          };

          // Also override any other potential endpoints
          config.api_host = proxyBase;
          config.cdn = proxyBase;
          config.cross_subdomain_cookie = false; // Prevent cross-domain issues

          logger.info("🔧 Mixpanel COMPLETE proxy configuration:", {
            origin: window.location.origin,
            endpoints: config.api_hosts,
            api_host: config.api_host,
            cdn: config.cdn,
            environment: mixpanelEnvironment,
          });
        } else {
          logger.info(
            "🔧 Mixpanel direct configuration (localhost detected):",
            {
              hostname: window.location.hostname,
              environment: "localhost",
            },
          );
        }

        mixpanel.init(mixpanelToken, config);
        if (isProductionMixpanel) {
          mixpanel.register({ session_replay_opt_in: true });
        }

        // Log successful initialization
        logger.info("📊 Mixpanel initialized successfully!", {
          token: mixpanelToken?.substring(0, 8) + "...",
          environment: mixpanelEnvironment,
          record_sessions_percent: sessionReplayPercent,
          config: {
            api_hosts: config.api_hosts || "default",
            debug: config.debug,
            persistence: config.persistence,
          },
        });

        this.initialized = true;
        return;
      } catch (error) {
        logger.error(`Mixpanel initialization attempt ${i + 1} failed:`, error);
        if (i === retries - 1) {
          logger.error(
            "All retries failed for Mixpanel initialization. Mixpanel will be disabled.",
          );
          throw error;
        }
      }
    }
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public getMixpanel() {
    return mixpanel;
  }

  public trackWithProxyStatus(eventName: string, properties?: any) {
    const isLocal = shouldUseDirectAnalytics();
    const proxyStatus = isLocal ? "localhost-direct" : "proxy-configured";

    const enhancedProperties = {
      ...properties,
      proxy_status: proxyStatus,
      origin: window.location.origin,
      user_agent: navigator.userAgent,
    };

    logger.info(
      `🎯 Tracking Mixpanel event "${eventName}" with proxy status: ${proxyStatus}`,
      enhancedProperties,
    );

    if (this.initialized) {
      mixpanel.track(eventName, enhancedProperties);
    } else {
      logger.warn("Mixpanel not initialized, cannot track event:", eventName);
    }
  }

  public getProxyStatus() {
    const isLocal = shouldUseDirectAnalytics();
    return {
      isLocalhost: isLocal,
      proxyConfigured: !isLocal,
      origin: window.location.origin,
      hostname: window.location.hostname,
      initialized: this.initialized,
    };
  }
}

export default MixpanelSingleton;
